import { pb } from "@/lib/pocketbase";
import { pbRepository } from "./pocketbase-repository";
import { auth } from "./auth.service";
import { DEFAULT_DAYS, DEFAULT_PERIODS, normalizePeriods } from "@/features/timetable/schedule";
import type { Period, TimetableSettings, TimetableSlot, Weekday } from "@/types/models";

const slotsRepo = pbRepository<TimetableSlot>("timetable_slots");

/**
 * PocketBase liefert JSON-Felder je nach Version als Objekt oder als String.
 * Beides wird hier auf einen sauberen Wert gebracht, damit die UI sich darauf
 * verlassen kann.
 */
function parseJson<T>(value: unknown, fallback: T): T {
  if (value == null || value === "") return fallback;
  if (typeof value === "string") {
    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  }
  return value as T;
}

/** Zeitraster für einen noch nicht gespeicherten Stand (kein Backend-Zugriff). */
export function defaultSettings(): TimetableSettings {
  return { periods: DEFAULT_PERIODS, days: DEFAULT_DAYS };
}

export const timetableService = {
  listSlots(): Promise<TimetableSlot[]> {
    return slotsRepo.list({ sort: "weekday,period" });
  },

  /**
   * Zeitraster des Nutzers. Liegt als JSON-Feld `timetableSettings` direkt
   * auf dem Auth-Record (siehe SCHEMA.md) – kein Serveraufruf nötig, die
   * Sitzung hat es schon geladen.
   */
  async getSettings(): Promise<TimetableSettings> {
    const raw = (auth.currentUser() as unknown as { timetableSettings?: unknown })?.timetableSettings;
    const parsed = parseJson<Partial<TimetableSettings>>(raw, {});
    const periods = normalizePeriods(parsed.periods ?? []);
    const days = parsed.days ?? [];
    return {
      periods: periods.length ? periods : DEFAULT_PERIODS,
      days: days.length ? days : DEFAULT_DAYS,
    };
  },

  async saveSettings(data: { periods: Period[]; days?: Weekday[] }): Promise<void> {
    const periods = normalizePeriods(data.periods);
    const payload: TimetableSettings = {
      periods: periods.length ? periods : DEFAULT_PERIODS,
      days: data.days?.length ? data.days : DEFAULT_DAYS,
    };
    const userId = auth.currentUser()?.id;
    if (!userId) return;
    await pb.collection("users").update(userId, { timetableSettings: payload });
  },

  /**
   * Belegt ein Feld. Ein bereits vorhandener Eintrag auf demselben Feld wird
   * überschrieben, damit pro Feld immer genau ein Fach steht.
   */
  async setSlot(
    weekday: Weekday,
    period: number,
    subject: string,
    room?: string
  ): Promise<TimetableSlot> {
    const existing = await slotsRepo.list({
      filter: pb.filter("weekday = {:w} && period = {:p}", { w: weekday, p: period }),
      perPage: 1,
    });
    if (existing.length) {
      return slotsRepo.update(existing[0].id, { subject, room: room ?? "" } as Partial<TimetableSlot>);
    }
    return slotsRepo.create({
      weekday,
      period,
      subject,
      room: room ?? "",
      owner: auth.currentUser()?.id,
    } as Partial<TimetableSlot>);
  },

  /**
   * Belegt ein Feld mit einem Termin OHNE Fach (z. B. freiwilliger Chor) –
   * ein bestehender Fach-Bezug wird dabei entfernt. Anders als bei einem
   * echten Fach gibt es hier nie ein Thema, also auch nie Materialien/
   * Mitschrift/Hausaufgaben dafür.
   */
  async setEventSlot(
    weekday: Weekday,
    period: number,
    event: { title: string; color: string; icon?: string },
    room?: string
  ): Promise<TimetableSlot> {
    const existing = await slotsRepo.list({
      filter: pb.filter("weekday = {:w} && period = {:p}", { w: weekday, p: period }),
      perPage: 1,
    });
    const data = {
      // Leerstring statt `null`, um die Relation zuverlässig zu leeren – bei
      // `null` reagiert PocketBase je nach Version unterschiedlich (Feld
      // bleibt teils unverändert stehen, oder die Anfrage braucht spürbar
      // länger, bis der Server sie beantwortet).
      subject: "",
      eventTitle: event.title,
      eventColor: event.color,
      eventIcon: event.icon ?? "",
      room: room ?? "",
    };
    if (existing.length) {
      return slotsRepo.update(existing[0].id, data as unknown as Partial<TimetableSlot>);
    }
    return slotsRepo.create({
      weekday,
      period,
      ...data,
      owner: auth.currentUser()?.id,
    } as unknown as Partial<TimetableSlot>);
  },

  /** Nur den Raum eines bestehenden Feldes ändern – Fach oder Termin bleiben unangetastet. */
  setRoom(id: string, room: string): Promise<TimetableSlot> {
    return slotsRepo.update(id, { room } as Partial<TimetableSlot>);
  },

  /**
   * Leert ein Feld. Gelöschte Fächer räumen ihre Slots dagegen von selbst ab
   * (cascadeDelete auf der Relation) – dafür braucht es hier nichts.
   */
  removeSlot(id: string): Promise<void> {
    return slotsRepo.remove(id);
  },
};
